/*!
 * YouTube facade — defer the iframe load until the user actually plays.
 *
 * Background:
 *   `external_video_tag` (Shopify Liquid filter) renders an `<iframe
 *   src="//www.youtube.com/embed/<id>">` eagerly on every page that has
 *   YouTube media. The browser then loads `www-widgetapi.vflset/www-
 *   widgetapi.js` (~300 KB) + multiple sub-resources, costing ~1.4 s
 *   of network time and 5-10+ requests EVEN IF THE USER NEVER PLAYS
 *   THE VIDEO. This is wasteful for a control most product pages
 *   never have user-engagement on.
 *
 *   The facade pattern (canonical: `lite-youtube-embed` by Paul
 *   Irish) replaces the iframe with a static thumbnail + play
 *   button. The real iframe is injected only on first user click.
 *   Same engagement, ~zero pre-click cost.
 *
 * Implementation:
 *   `snippets/media.liquid` emits `<div class="youtube-facade"
 *   data-yt-id="…" data-yt-loop="0|1">` containing the thumbnail
 *   image + a play button. This script listens for a click on
 *   `.youtube-facade__play` (or anywhere in the facade as a fallback)
 *   and swaps the contents for the real iframe with autoplay=1.
 *
 *   Uses `youtube-nocookie.com/embed/…` (privacy-enhanced mode) per
 *   YouTube's recommendation — doesn't drop persistent cookies until
 *   the user actually plays. Same behaviour & API as standard embeds.
 *
 *   Idempotent via `window.__youtubeFacadeInit`. Listener is delegated
 *   on document so it survives Stiletto's section re-renders + works
 *   for facades injected dynamically (e.g. PDP gallery swap).
 */

(function () {
  if (window.__youtubeFacadeInit) return;
  window.__youtubeFacadeInit = true;

  function activate(facade) {
    if (!facade || facade.dataset.ytActivated === 'true') return;
    var ytId = facade.getAttribute('data-yt-id');
    if (!ytId) return;

    var loop = facade.getAttribute('data-yt-loop') === '1';
    var params = [
      'autoplay=1',
      'playsinline=1',
      'rel=0',
      loop ? ('loop=1&playlist=' + encodeURIComponent(ytId)) : null
    ].filter(Boolean).join('&');

    var iframe = document.createElement('iframe');
    iframe.setAttribute('src', 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(ytId) + '?' + params);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('title', 'YouTube video player');
    iframe.style.position = 'absolute';
    iframe.style.inset = '0';
    iframe.style.width = '100%';
    iframe.style.height = '100%';

    // Replace facade content with iframe
    facade.innerHTML = '';
    facade.appendChild(iframe);
    facade.dataset.ytActivated = 'true';
    facade.classList.add('youtube-facade--active');
  }

  // Click delegation. Activate on click anywhere on the facade or its play button.
  document.addEventListener('click', function (e) {
    var facade = e.target.closest('[data-yt-facade]');
    if (!facade) return;
    e.preventDefault();
    activate(facade);
  }, true);

  // Optional: warm up the YouTube origin on hover so the iframe load
  // starts ~100 ms before the user's click. Negligible cost (a single
  // preconnect handshake), measurable click→play improvement.
  function warmYoutubeOrigin() {
    if (window.__youtubeFacadeWarmed) return;
    window.__youtubeFacadeWarmed = true;
    ['https://www.youtube-nocookie.com', 'https://www.google.com'].forEach(function (origin) {
      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }
  document.addEventListener('mouseover', function (e) {
    if (e.target.closest('[data-yt-facade]')) warmYoutubeOrigin();
  }, { passive: true, capture: true });
  document.addEventListener('touchstart', function (e) {
    if (e.target.closest('[data-yt-facade]')) warmYoutubeOrigin();
  }, { passive: true, capture: true });
})();
